import React, { createContext, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { ENDPOINT_ORDERS, ORDER_STATUS } from '@/common/constants';
import { useAuth } from '@/common/contexts/AuthContext';
import useOrderSubscription from '@/common/hooks/mercure/useOrderSubscription';
import useUpdateReference from '@/common/mutations/orders/useUpdateReference';
import useInvoices from '@/common/queries/invoices/useInvoices';
import useOrder from '@/common/queries/orders/useOrder';
import useWorksite from '@/common/queries/worksites/useWorksite';

import {
  OrderContextProviderDefaultProps,
  OrderContextProviderPropTypes,
} from './OrderContextProvider.proptypes';

const OrderContext = createContext(null);

const OrderContextProvider = ({ children }) => {
  const { id } = useParams();
  const { user, zergRush } = useAuth();

  const queryKey = [ENDPOINT_ORDERS, id];

  const {
    data: order,
    isFetching,
    isLoading,
    isError,
    isSuccess,
    refetch,
  } = useOrder(id);
  const { data: worksite } = useWorksite(order?.worksite?.code);
  const {
    data: invoices,
    isLoading: isLoadingInvoice,
    isFetching: isFetchingInvoice,
  } = useInvoices(order?.has_invoice && order?.number);
  const { mutate: mutateReference } = useUpdateReference(queryKey);
  const handleUpdateReference = (reference, onError, onSuccess) =>
    mutateReference({ id, reference }, { onError, onSuccess });

  useOrderSubscription(queryKey);

  const isDisabled = useMemo(() => {
    const loading =
      isFetching || isLoading || isLoadingInvoice || isFetchingInvoice;
    const blocked = loading;

    return {
      disabled: blocked,
      info: order?.status?.code === ORDER_STATUS.BLOCKED || blocked || zergRush,
      loading,
      print: blocked || zergRush || !user?.functionality_accesses?.orders_pdf,
      reference:
        order?.has_invoice ||
        blocked ||
        zergRush ||
        !user?.functionality_accesses?.orders_update,
    };
  }, [
    isFetching,
    isFetchingInvoice,
    isLoading,
    isLoadingInvoice,
    order,
    user,
    zergRush,
  ]);

  return (
    <OrderContext.Provider
      value={{
        data: order,
        handleUpdateReference,
        invoices,
        isDisabled,
        isError,
        isSuccess,
        refetch,
        worksite,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
};

OrderContextProvider.defaultProps = {
  ...OrderContextProviderDefaultProps,
};

OrderContextProvider.propTypes = {
  ...OrderContextProviderPropTypes,
};

export const useOrderContext = () => useContext(OrderContext);

export default OrderContextProvider;
